import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/joy";
import PaymentIcon from "@mui/icons-material/Payment";
import { Link } from "react-router-dom";
import Page from "../components/Pages/Page";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import { useOrgSettingsContext } from "../providers/OrgSettingsProvider";

const OrgSettingsOverview: React.FC = () => {
  const { organizationDetails, loadingOrgDetails, isEnterpriseMode } =
    useOrgSettingsContext();

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">Overview</Typography>
        <Typography level="body-sm" color="neutral">
          Organization details and account settings
        </Typography>
      </PageHeader>

      <Stack spacing={3}>
        {/* Organization Details */}
        <Card>
          <CardContent>
            <Typography level="h3" sx={{ mb: 3 }}>
              Organization Details
            </Typography>

            {loadingOrgDetails ? (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <Loading />
              </Box>
            ) : (
              <Stack spacing={2}>
                <Box>
                  <Typography level="body-sm" color="neutral" sx={{ mb: 0.5 }}>
                    Organization Name
                  </Typography>
                  <Typography level="title-lg" sx={{ fontWeight: 600 }}>
                    {organizationDetails?.name || "Unknown Organization"}
                  </Typography>
                </Box>
                <Box>
                  <Typography level="body-sm" color="neutral" sx={{ mb: 0.5 }}>
                    Status
                  </Typography>
                  <Chip
                    size="sm"
                    variant="soft"
                    color={
                      organizationDetails?.status === "active"
                        ? "success"
                        : organizationDetails?.status === "pending_payment"
                          ? "warning"
                          : organizationDetails?.status === "pending_setup"
                            ? "neutral"
                            : "danger"
                    }
                  >
                    {organizationDetails?.status === "active"
                      ? "Active"
                      : organizationDetails?.status === "pending_payment"
                        ? "Pending Payment"
                        : organizationDetails?.status === "pending_setup"
                          ? "Setup Required"
                          : "Suspended"}
                  </Chip>
                </Box>
              </Stack>
            )}

            {/* Billing setup prompt — hidden in Enterprise mode */}
            {!isEnterpriseMode &&
              !loadingOrgDetails &&
              organizationDetails?.status &&
              organizationDetails.status !== "active" && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Alert
                    color={
                      organizationDetails.status === "pending_payment"
                        ? "warning"
                        : "neutral"
                    }
                    variant="soft"
                  >
                    <Stack spacing={2}>
                      <Box>
                        <Typography
                          level="body-sm"
                          sx={{ mb: 1, fontWeight: 600 }}
                        >
                          {organizationDetails.status === "pending_payment"
                            ? "Complete Billing Setup"
                            : organizationDetails.status === "pending_setup"
                              ? "Setup Required"
                              : "Organization Suspended"}
                        </Typography>
                        <Typography level="body-sm">
                          {organizationDetails.status === "pending_payment"
                            ? "Choose a billing package to activate your organization and start inviting users."
                            : organizationDetails.status === "pending_setup"
                              ? "Complete the setup process to activate your organization."
                              : "Your organization has been suspended. Please contact support for assistance."}
                        </Typography>
                      </Box>
                      {(organizationDetails.status === "pending_payment" ||
                        organizationDetails.status === "pending_setup") && (
                        <Button
                          variant="solid"
                          color="primary"
                          component={Link}
                          to="/subscription/select-package"
                          startDecorator={<PaymentIcon />}
                          size="sm"
                          sx={{ alignSelf: "flex-start" }}
                        >
                          Go to Billing & Subscriptions
                        </Button>
                      )}
                    </Stack>
                  </Alert>
                </>
              )}
          </CardContent>
        </Card>

      </Stack>
    </Page>
  );
};

export default OrgSettingsOverview;
